import PropTypes from 'prop-types';
import { constants } from '../../../config/constants';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { noop } from '../../../utils/noop';
import { SkipSSR } from '../../../utils/SkipSSRFacets';
import Accordion from '../../ui/Accordion/Accordion';
import { resetDigitalData } from '../Common/digitalData';
import { getFacetsData } from '../Common/getFacetsData';
import { facetClick } from '../FacetEvents';
import { FacetsWithSwitch } from '../FacetsWithSwitch/FacetsWithSwitch';
import { facetsDesktopStyles } from './FacetsDesktop.style';

const RenderFacet = ({ children, variant, type }) => (
  <div className={`facetsDesktop ${variant}`} id={`testId-${type}-desktop-container`}>
    <div className="facetsDesktop-body">{children}</div>
    <style jsx>{facetsDesktopStyles}</style>
  </div>
);
RenderFacet.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

const FacetsDesktop = ({
  data,
  dataMethods,
  freeShippingFacet,
  pageName,
  appCtx,
  onFacetChangeHandler,
  getUpdatedFacets,
  canonicalUrl,
}) => {
  if (!data.length) return null;
  const { type, tenant, baseFacetsURL, changeURL } = dataMethods;
  const labels = appCtx?.siteConfig?.textDictionary || {};
  const searchInFacetsLabel = labels.SEARCH_IN_FACETS_LABEL;
  const priceRangeErrorLabel = labels.ENTER_VALID_PRICE_ERROR_LABEL;
  const priceRangeNoResultsLabel = labels.PRICE_NO_RESULTS_ERROR_LABEL;

  const popularBrandFacet = data.find(
    (facet) => constants.SWITCH_TOGGLE_REGEX.test(facet.type) && facet?.name?.toLowerCase() === constants.POPULAR_BRAND
  );

  const regularFacets = data.filter(
    (facet) =>
      !(constants.SWITCH_TOGGLE_REGEX.test(facet.type) && facet?.name?.toLowerCase() === constants.POPULAR_BRAND)
  );

  const hasCustomVisualsForItem = () => {
    return type === 'availability';
  };

  const isNewOmniChannelExperience = appCtx?.siteConfig?.toggles?.isNewOmniChannelExperience ?? true;

  const onFacetChange = (obj, resetDD = true) => {
    facetClick();
    const { url } = obj;
    const urls = [];
    if (baseFacetsURL) {
      urls.push(baseFacetsURL);
    }
    if (url) {
      const facetURL = url.split('&').filter((u) => !u.match(/facetSelected/g));
      if (facetURL.length > 0) {
        urls.push('facetSelected=true', facetURL.join('&'));
      }
    }
    changeURL(urls.join('&'));
    onFacetChangeHandler();
    if (resetDD) {
      resetDigitalData();
    }
  };

  const facetData = regularFacets
    .map(
      getFacetsData({
        groupType: type,
        freeShippingFacet,
        tenant,
        onFacetChange,
        baseFacetsURL,
        changeURL,
        priceRangeErrorLabel,
        priceRangeNoResultsLabel,
        searchInFacetsLabel,
        pageName,
        hideMenuFacets: noop,
        appCtx,
        getUpdatedFacets,
        canonicalUrl,
        popularBrandFacet,
      })
    )
    .map((item) => {
      // eslint-disable-next-line no-param-reassign
      item.content = item.state ? item.content : SkipSSR(item.content);
      return item;
    });

  switch (type) {
    case 'facetsWithSwitch': {
      return <FacetsWithSwitch items={facetData} />;
    }
    case 'availability': {
      if (isNewOmniChannelExperience) {
        return (
          <RenderFacet variant="shipping" type={type}>
            <Accordion items={facetData} hasCustomVisualsForItem={hasCustomVisualsForItem()} />
          </RenderFacet>
        );
      }
      return (
        <RenderFacet variant="shipping" type={type}>
          {facetData?.[0]?.content()}
        </RenderFacet>
      );
    }
    default:
      return (
        <RenderFacet type={type}>
          <Accordion items={facetData} />
        </RenderFacet>
      );
  }
};

FacetsDesktop.defaultProps = {
  pageName: 'search',
  data: [],
  canonicalUrl: '',
  freeShippingFacet: { values: [] },
};

FacetsDesktop.propTypes = {
  dataMethods: PropTypes.object.isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  appCtx: PropTypes.object.isRequired,
  pageName: PropTypes.string,
  onFacetChangeHandler: PropTypes.func.isRequired,
  getUpdatedFacets: PropTypes.func.isRequired,
  canonicalUrl: PropTypes.string,
  freeShippingFacet: PropTypes.shape({ facets: PropTypes.array }),
};

export default withApplicationContext(FacetsDesktop);
export { FacetsDesktop };
